import classNames from 'classnames';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { loadLinkToolCss } from '../../editor/editorjs/load-link-tool-css';
import { useConnectedRouter } from '../../hooks/useConnectedRouter';
// import { useConnectedRouter } from '../../hooks/useConnectedRouter';
import { useWebsite } from '../../hooks/useWebsite';
import type { Author, Vote } from '../../types/api';
import { NoSSR } from '../../utils/ssr';
import { isMobile } from '../../utils/web';
import { MessageContent } from '../Content/MessageContent/MessageContent';
import { MessageLimiter } from '../Content/MessageLimiter';
import { MessageLimiterDomParser } from '../Content/MessageLimiterDomParser';
import { ThreadOptionMenu } from '../HeaderActions/ThreadOptionMenu';
import { ThreadAuthoringDetails } from '../ThreadAuthoringDetails';
import { useThreadIsRoot } from '../ThreadContexts/ThreadIsRootContext';
import { getVoteIcon } from '../Vote/icons';

loadLinkToolCss();

export const ThreadInteractiveContent: React.FC<{
  messageId?: string | undefined;
  mode?: 'nested';
  author: Author;
  answeredAuthor?: Author;
  date: Date;
  content: string;
  embeds: string | undefined;
  status: string;
  votes: Vote[];
  options: Record<string, React.ReactNode>;
  actions?: React.ReactNode;
  topIcon?: React.ReactNode;
  unlimited?: boolean;
  heightLimit?: number;
  topicSource?: React.ReactNode;
  isTopicMsg?: boolean;
  isNestedAnswer?: boolean;
}> = ({
  messageId,
  mode,
  author,
  answeredAuthor,
  date,
  content,
  embeds,
  status,
  isTopicMsg,
  votes: rawVotes = [],
  options,
  actions,
  topIcon,
  unlimited,
  heightLimit = isMobile() ? 320 : 288,
  topicSource,
  isNestedAnswer,
}) => {
  const { graphdebate = {} } = useWebsite();
  const { query } = useConnectedRouter();
  const isRelevanceSort = query?.sort === 'relevance';

  const { votesDiscreet: isVoteDiscreet } = graphdebate ?? {};
  const votes = (rawVotes || []).filter(vote =>
    isVoteDiscreet ? (vote.score as number) > 0 : vote.score !== 0,
  );

  const mainScores = Object.entries(
    votes.reduce(
      (prev, vote) => ({
        ...prev,
        [`${vote.score}`]: (prev[`${vote.score}`] || 0) + 1,
      }),
      {} as Record<string, number>,
    ),
  )
    .map(([score]) => parseInt(score, 10))
    .slice(0, 3);

  const contentElt = <MessageContent content={content} embeds={embeds} status={status} withMedia />;

  const isDeleted = status === 'deleted' || status === 'refused';
  const isRoot = useThreadIsRoot();

  return (
    <div className="ThreadInteractiveContent editor space-y-2.5">
      {/* items-center relative -mr-1 */}
      <div className="ThreadInteractiveContentHeader flex justify-between gap-x-2">
        <div className={'threadMsgChildAuthor flex flex-1 min-w-0'}>
          <div className="flex space-x-2 min-w-0">
            <div
              className={twMerge(
                'AuthoringDetails flex items-center gap-2 min-w-0',
                !!answeredAuthor && 'flex-col items-start',
                isNestedAnswer && !isRoot && 'flex-col items-start gap-0',
                !isRelevanceSort && 'items-start',
              )}
            >
              <ThreadAuthoringDetails
                date={date}
                author={author}
                answeredAuthor={answeredAuthor}
                isNestedAnswer={isNestedAnswer}
                noCenter
                isRelevanceSort={isRelevanceSort}
              />
            </div>
          </div>
        </div>

        <div
          className={twMerge(
            'threadMessageChildActions flex items-center -mt-3 -mr-3 md:-mt-1 md:-mr-1',
            answeredAuthor && 'items-start',
            mode,
            isNestedAnswer ? 'isNestedAnswer' : 'isNotNestedAnswer',
          )}
        >
          <div className="ThreadOptionMenu flex flex-col-reverse md:flex-row items-center justify-center relative">
            {topIcon}
            <NoSSR>
              <ThreadOptionMenu messageId={messageId} {...options} />
            </NoSSR>
          </div>
        </div>
      </div>

      <div className="ThreadMessageContent relative flex-1">
        {unlimited ? (
          contentElt
        ) : (
          <>
            {isTopicMsg ? (
              <MessageLimiter limit={heightLimit}>{contentElt}</MessageLimiter>
            ) : (
              <MessageLimiterDomParser contentString={content} embeds={embeds}>
                {contentElt}
              </MessageLimiterDomParser>
            )}
          </>
        )}

        <div
          className={classNames(
            'flex items-center justify-between gap-2 mt-1',
            isDeleted && 'pointer-events-none opacity-0',
          )}
        >
          {votes.length !== 0 && (
            <div className="flex items-center gap-2">
              <div className="flex [&>*+*]:ml-[-5px]">
                {mainScores.map((score, index) => (
                  <div
                    // eslint-disable-next-line
                  key={index}
                    className="flex justify-center items-center bg-white shadow p-[3px] rounded-full"
                  >
                    {getVoteIcon(score)}
                  </div>
                ))}
              </div>
              <div className="text-gray-medium">{votes.length}</div>
            </div>
          )}
          {topicSource}
          <div className="actionsContainer flex justify-end flex-1 text-base flex-wrap text-gray-silver">
            {actions}
          </div>
        </div>
      </div>
    </div>
  );
};
